(() => {
  if (customElements.get('amaro-combos')) return;
  const money = (cents, currency) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN', maximumFractionDigits: 0 }).format(cents / 100);
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  let catalogPromise, cachedCatalog, catalogCheckedAt = 0;
  const catalogListeners = new Set();
  const stock = (variant) => variant.available === true ? variant.stock == null ? Infinity : Math.max(0, Math.floor(Number(variant.stock) || 0)) : 0;
  const availability = (products) => {
    const units = { small: 0, large: 0, collab: 0 };
    for (const product of products) {
      if (!(product.group in units)) continue;
      for (const variant of product.variants) units[product.group] = Math.min(3, units[product.group] + stock(variant));
    }
    return { three: units.small >= 3, two: units.small >= 1 && units.large >= 1, gift: units.small >= 1 && units.collab >= 1 };
  };
  const getCatalog = (root, fresh = false) => {
    if (!fresh && cachedCatalog && Date.now() - catalogCheckedAt < 30000) return Promise.resolve(cachedCatalog);
    if (!catalogPromise) {
      catalogPromise = (async () => {
        const url = new URL(root, location.origin);
        url.searchParams.set('section_id', 'amaro-combo-data');
        url.searchParams.set('_amaro_stock', Date.now());
        let catalog, page = 1;
        do {
          url.searchParams.set('page', page);
          const response = await fetch(url, { cache: 'no-store' });
          if (!response.ok) throw new Error('catalog');
          const html = await response.text();
          const data = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-amaro-combo-catalog]');
          const part = JSON.parse(data?.textContent || 'null');
          if (!Array.isArray(part?.products)) throw new Error('catalog');
          if (!catalog) catalog = { ...part, products: [] };
          catalog.products.push(...part.products);
          const next = Number(part.nextPage) || 0;
          if (next && next <= page) throw new Error('catalog');
          page = next;
        } while (page);
        cachedCatalog = catalog;
        catalogCheckedAt = Date.now();
        for (const listener of catalogListeners) listener(catalog);
        return catalog;
      })().catch((error) => {
        // Preserve the last selection when a background tab temporarily loses its connection.
        catalogCheckedAt = 0;
        throw error;
      }).finally(() => { catalogPromise = null; });
    }
    return catalogPromise;
  };

  // Reserve collaboration gifts first, then 2x999 companions. Each pair is allocated once.
  const plan = (items, products) => {
    const small = [], large = [], collab = [], sizes = new Set(), inCart = new Set(), inCartModels = new Set();
    let ignored = 0;
    const remainingStock = new Map();
    const byId = new Map(products.map((product) => [Number(product.id), product]));
    for (const item of items) {
      const product = byId.get(Number(item.product_id));
      const quantity = Math.max(0, Math.floor(Number(item.quantity) || 0));
      if (!product || !['small', 'large', 'collab'].includes(product.group) || item.selling_plan_allocation) { ignored += quantity; continue; }
      const variant = product.variants.find((entry) => Number(entry.id) === Number(item.variant_id || item.id));
      if (!variant || !stock(variant)) { ignored += quantity; continue; }
      const key = Number(variant.id);
      const remaining = remainingStock.has(key) ? remainingStock.get(key) : stock(variant);
      const usable = Math.min(quantity, remaining);
      remainingStock.set(key, remaining - usable);
      ignored += quantity - usable;
      if (!usable) continue;
      inCart.add(Number(product.id));
      inCartModels.add(product.title.trim().toLowerCase());
      for (const option of item.options_with_values || []) {
        if (/talla|size|tamañ/i.test(option.name)) sizes.add(String(option.value));
      }
      const price = Number(item.original_price ?? item.price ?? variant.price);
      const target = product.group === 'collab' ? collab : product.group === 'small' ? small : large;
      for (let i = 0; i < usable; i++) target.push(price);
    }
    small.sort((a, b) => b - a); large.sort((a, b) => a - b);
    const gifts = Math.min(collab.length, small.length);
    // Gifts reserve the most expensive units. Bundler groups remaining units cheapest first.
    const companions = small.slice(gifts).sort((a, b) => a - b);
    const pairs = Math.min(companions.length, large.length);
    let lowerPriceGroups = 0;
    for (let i = 0; i < pairs; i++) if (companions[i] + large[i] <= 99900) lowerPriceGroups++;
    const remaining = companions.slice(pairs);
    const triples = Math.floor(remaining.length / 3);
    for (let i = 0; i < triples; i++) if (remaining.slice(i * 3, i * 3 + 3).reduce((a, b) => a + b, 0) <= 99900) lowerPriceGroups++;
    return { gifts, collab: collab.length - gifts, collaborations: collab.length, pairs, triples, small: remaining.length % 3, large: large.length - pairs, sizes, inCart, inCartModels, ignored, lowerPriceGroups };
  };

  const selectionDestination = (choice, state, root = '/') => {
    const view = choice === 'gift' ? 'amaro-regalos' : choice === 'two' ? state.large ? 'amaro-segundo-par' : 'amaro-completa' : 'amaro-tres-favoritos';
    const label = choice === 'gift' ? 'Ver regalos disponibles' : choice === 'two' ? 'Ver pares para combinar' : 'Ver más modelos';
    return { href: `${root}collections/all?view=${view}`, label };
  };
  const promoTitles = { two: '2 pares por $999', three: '3 pares por $999', gift: 'Tu par + regalo' };
  const completedCount = (choice, state) => choice === 'gift' ? state.gifts : choice === 'two' ? state.pairs : state.triples;
  const rememberChoice = choice => { try { sessionStorage.setItem('amaro-combo-choice', choice); } catch (_) {} };
  const preferredChoice = (state, available) => {
    let saved; try { saved = sessionStorage.getItem('amaro-combo-choice'); } catch (_) {}
    return state.collab && available.gift ? 'gift' : state.large && available.two ? 'two' : state.small && !['two', 'three'].includes(saved) ? available.three ? 'three' : 'two' : available[saved] ? saved : state.gifts && available.gift ? 'gift' : state.pairs && available.two ? 'two' : available.three ? 'three' : available.two ? 'two' : 'gift';
  };
  const journey = (choice, state) => {
    const total = choice === 'three' ? 3 : 2;
    const count = completedCount(choice, state);
    const pending = choice === 'gift' ? state.collab : choice === 'two' ? Number(Boolean(state.large || state.small)) : state.small;
    const complete = count > 0 && pending === 0;
    const filled = complete ? total : choice === 'gift' || choice === 'two' ? Number(Boolean(pending)) : pending;
    const group = choice === 'gift' ? state.collab ? 'small' : 'collab' : choice === 'two' ? state.large ? 'small' : 'large' : 'small';
    const title = complete ? 'Combo completo' : choice === 'gift' ? state.collab ? 'Elige tu regalo' : 'Elige tu par con regalo' : choice === 'two' ? filled ? 'Elige tu segundo par' : 'Elige tu primer par' : `Elige tu ${filled === 0 ? 'primer' : filled === 1 ? 'segundo' : 'tercer'} par`;
    const instruction = complete ? 'Revisa tus pares y el total en la bolsa. También puedes armar otro combo.' : choice === 'gift' ? state.collab ? `${state.collab > 1 ? `Tienes ${state.collab} regalos por elegir.` : 'Este par va de regalo.'} Elige modelo y talla.` : 'Elige un modelo de Isa Martínez o Isabel Salas. Después podrás elegir tu regalo.' : choice === 'two' ? state.large ? 'Elige una sandalia, mule o balerina de la selección.' : 'Elige una zapatilla, bota o sneaker de la selección.' : `Te ${3 - filled === 1 ? 'falta 1 par' : `faltan ${3 - filled} pares`} de la selección.`;
    return { total, count, complete, filled, group, title, instruction };
  };
  const progressMarkup = (choice, flow) => `<p class="amaro-flow-label">${promoTitles[choice]}<span>${flow.filled}/${flow.total} pares</span></p><ol class="amaro-flow-progress" aria-label="${flow.filled} de ${flow.total} pares elegidos">${Array.from({ length: flow.total }, (_, i) => `<li class="${i < flow.filled ? 'is-filled' : ''}">${i < flow.filled ? '✓' : i + 1}</li>`).join('')}</ol>`;
  const rulesMarkup = '<details class="amaro-flow-rules"><summary>Cómo se aplican las promociones</summary><p>Agrega los pares de la selección a tu bolsa. Los descuentos se calculan automáticamente; cada par participa en una sola promoción. Puedes elegir tallas diferentes.</p></details>';
  const inlineSizeError = (button, select) => {
    const host = button.closest('article,[data-amaro-card]') || button.parentElement;
    let error = host.querySelector('.amaro-size-error');
    if (!error) { error = document.createElement('p'); error.className = 'amaro-size-error'; error.setAttribute('role', 'alert'); (button.closest('.amaro-combos-buy') || button).before(error); select?.addEventListener('change', () => error.remove(), { once: true }); }
    error.textContent = 'Elige tu talla antes de agregar.';
    requestAnimationFrame(() => {
      const summary = select?.nextElementSibling?.querySelector('summary');
      const target = summary || select || host.querySelector('[data-context-size]:not(:disabled)');
      target?.focus(); if (summary) summary.parentElement.open = true;
    });
  };
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href*="view=amaro-"]'); if (!link) return;
    const view = new URL(link.href, location.origin).searchParams.get('view');
    rememberChoice(view === 'amaro-regalos' ? 'gift' : view === 'amaro-tres-favoritos' ? 'three' : 'two');
  });
  const selectionContext = (mode, items, products, originId) => {
    const state = plan(items, products);
    const choice = mode === 'gift' ? 'gift' : mode === 'three' ? 'three' : 'two';
    const group = journey(choice, state).group;
    const complete = mode === 'gift' ? state.gifts > 0 && state.collab === 0 : mode === 'three' ? state.triples > 0 && state.small === 0 : state.pairs > 0 && state.large === 0 && state.small === 0;
    const trigger = mode === 'gift' ? state.collaborations > 0 : mode === 'three' ? state.small > 0 : group === 'small' ? state.large > 0 : state.small > 0;
    const origin = products.find(product => Number(product.id) === Number(originId) && (choice === 'gift' ? product.group === 'collab' : choice === 'three' ? product.group === 'small' : ['small', 'large'].includes(product.group)) && product.variants.some(variant => stock(variant) > 0));
    const chosen = items.filter((item) => products.some((product) => Number(product.id) === Number(item.product_id) && (choice === 'gift' ? product.group === 'collab' : choice === 'two' ? ['small', 'large'].includes(product.group) : product.group === 'small')));
    return { state, choice, group, complete, trigger, origin, chosen };
  };

  // Both home rails and contextual pages resolve the size against the active color.
  const renderCardPurchase = (card, product) => {
    card.querySelector('.amaro-size-error')?.remove();
    let buy = card.querySelector('[data-context-buy]');
    if (!buy) { buy = document.createElement('div'); buy.className = 'amaro-context-buy'; buy.setAttribute('data-context-buy', ''); card.append(buy); }
    let selected;
    for (const size of card.querySelectorAll('[data-context-size]')) {
      const variant = product?.variants.find((entry) => Number(entry.id) === Number(size.dataset.variantId) && stock(entry) > 0);
      size.disabled = !variant;
      size.classList.toggle('amaro-size-unavailable', !variant);
      const pressed = Boolean(variant && size.dataset.contextSize === card.dataset.selectedSize);
      size.setAttribute('aria-pressed', String(pressed));
      if (pressed) selected = variant;
    }
    if (!selected) delete card.dataset.selectedSize;
    if (!product) { buy.innerHTML = '<p>Este color no tiene existencias. Elige otro color disponible.</p>'; return; }
    if (Number(product.id) === 15877783257374) {
      const quickView = card.querySelector('[data-quickview]');
      if (quickView) {
        const soldOut = product.variants.length > 0 && !product.variants.some((variant) => stock(variant) > 0);
        buy.innerHTML = `<div class="amaro-combos-buy"><span class="amaro-context-selection">Elige la talla de cada par</span><button type="button" data-combo-size-options${soldOut ? ' disabled' : ''} aria-label="Elegir tallas de ${escape(product.title)}">${soldOut ? 'Agotado' : 'Elegir tallas'}</button></div>`;
        buy.querySelector('[data-combo-size-options]').addEventListener('click', () => quickView.click());
        return;
      }
    }
    const color = card.querySelector('[data-amaro-color-name]')?.textContent.trim();
    const label = selected ? `Talla ${escape(card.dataset.selectedSize)}${color ? ` · ${escape(color)}` : ''}` : 'Elige tu talla arriba';
    buy.innerHTML = `<div class="amaro-combos-buy"><span class="amaro-context-selection">${label}</span><button type="button" data-combo-add="${product.id}" data-combo-variant="${selected?.id || ''}" aria-label="Agregar ${escape(product.title)}${selected ? `, talla ${escape(card.dataset.selectedSize)}` : ''}">Agregar</button></div>`;
  };

  class AmaroCombos extends HTMLElement {
    connectedCallback() {
      this.sequence = 0;
      this.root = this.dataset.root || '/';
      this.content = this.querySelector('[data-combo-content]');
      this.status = this.querySelector('[data-combo-status]');
      try { this.items = JSON.parse(this.querySelector('[data-combo-cart]').textContent); }
      catch (_) { return; }
      this.abort = new AbortController();
      // Size selectors live inside cart-items; they must not trigger a cart quantity update.
      this.addEventListener('change', (event) => event.stopPropagation(), { signal: this.abort.signal });
      this.addEventListener('click', (event) => {
        const choice = event.target.closest('[data-combo-choice]');
        if (choice) { this.choice = choice.dataset.comboChoice; rememberChoice(this.choice); this.render(); }
        if (event.target.closest('[data-combo-retry]')) this.refresh();
        const add = event.target.closest('[data-combo-add]');
        if (add) this.addProduct(add);
      }, { signal: this.abort.signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.refresh(event.cartState));
      }
      const refreshVisible = () => {
        if (document.visibilityState !== 'visible' || Date.now() - catalogCheckedAt < 30000) return;
        const drawer = this.closest('cart-drawer');
        if (!drawer || drawer.classList.contains('open')) this.refresh();
      };
      document.addEventListener('visibilitychange', refreshVisible, { signal: this.abort.signal });
      window.addEventListener('focus', refreshVisible, { signal: this.abort.signal });
      window.addEventListener('online', refreshVisible, { signal: this.abort.signal });
      this.stockTimer = setInterval(refreshVisible, 300000);
      // Preparing a closed drawer competes with the visible product for CPU/network.
      this.drawer = this.closest('cart-drawer');
      if (this.drawer) {
        let wasOpen = this.drawer.classList.contains('open');
        this.drawerObserver = new MutationObserver(() => {
          const isOpen = this.drawer.classList.contains('open');
          if (isOpen && !wasOpen) this.refresh(this.pendingCart);
          wasOpen = isOpen;
        });
        this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
        if (!wasOpen) return;
      }
      getCatalog(this.root, true).then((catalog) => {
        if (!this.isConnected) return;
        this.catalog = catalog;
        this.render();
      }).catch(() => { if (this.isConnected) { if (cachedCatalog) { this.catalog = cachedCatalog; this.render(); } else this.unavailable(); } });
    }
    disconnectedCallback() { this.sequence++; this.abort?.abort(); this.unsubscribe?.(); clearInterval(this.stockTimer); this.drawerObserver?.disconnect(); }
    unavailable() {
      if (this.catalog) return;
      this.choice = undefined;
      this.content.innerHTML = `<p class="amaro-combos-note">No pudimos cargar las opciones de combo.</p><button class="amaro-flow-text-button" type="button" data-combo-retry>Reintentar</button><a class="amaro-combos-more" href="${this.root}collections/all">Seguir viendo productos →</a>`;
    }
    async refresh(cart) {
      const sequence = ++this.sequence;
      if (this.drawer && !this.drawer.classList.contains('open')) {
        this.pendingCart = cart;
        return;
      }
      try {
        if (!Array.isArray(cart?.items)) {
          const response = await fetch(`${this.root}cart.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error('cart');
          cart = await response.json();
        }
        const catalog = await getCatalog(this.root, true);
        if (!this.isConnected || sequence !== this.sequence) return;
        if (this.drawer && !this.drawer.classList.contains('open')) { this.pendingCart = cart; return; }
        this.pendingCart = undefined;
        this.items = cart.items;
        this.catalog = catalog;
        this.status.textContent = '';
        this.render();
      } catch (_) {
        if (this.isConnected && sequence === this.sequence) {
          this.unavailable();
          if (!this.catalog && document.visibilityState === 'visible') this.status.textContent = 'Reconectando la selección. Intenta de nuevo al recuperar la conexión.';
        }
      }
    }
    render() {
      const state = plan(this.items, this.catalog.products);
      this.state = state;
      const available = availability(this.catalog.products);
      const choices = ['three', 'two', 'gift'].filter(choice => available[choice]);
      if (!choices.length) { this.unavailable(); return; }
      if (!available[this.choice]) this.choice = preferredChoice(state, available);
      const flow = journey(this.choice, state), currency = this.catalog.currency;
      const selected = this.catalog.products.filter(product => product.group === flow.group && Number(product.id) !== 15877783257374 && !state.inCartModels.has(product.title.trim().toLowerCase()) && product.variants.some(variant => stock(variant) > 0));
      selected.sort((a, b) => {
        const matches = product => product.variants.some(variant => stock(variant) > 0 && variant.options.some(option => state.sizes.has(String(option))));
        return Number(matches(b)) - Number(matches(a));
      });
      const cards = !flow.complete ? selected.filter((product, i, products) => products.findIndex(other => other.title === product.title) === i).slice(0, 3).map(product => {
        const variants = product.variants.filter(variant => stock(variant) > 0);
        const gift = this.choice === 'gift' && flow.group === 'small';
        return `<article class="amaro-combos-product"><a href="${escape(product.url)}" tabindex="-1" aria-hidden="true">${product.image ? `<img class="amaro-combos-image" src="${escape(product.image)}" alt="" width="78" height="98" loading="lazy">` : ''}</a><div><a href="${escape(product.url)}">${escape(product.title)}</a><span class="amaro-combos-price">${gift ? 'Tu regalo · sin costo' : `Desde ${money(Math.min(...variants.map(variant => variant.price)), currency)}`}</span><div class="amaro-combos-buy"><select aria-label="Talla y color de ${escape(product.title)}"><option value="">Elige talla</option>${variants.map(variant => `<option value="${variant.id}">${escape(variant.title)}</option>`).join('')}</select><button type="button" data-combo-add="${product.id}">Agregar</button></div></div></article>`;
      }).join('') : '';
      const destination = selectionDestination(this.choice, state, this.root);
      const completed = [['gift', state.gifts], ['two', state.pairs], ['three', state.triples]].filter(([, count]) => count).map(([choice, count]) => `<li>${choice === 'gift' ? `${count} ${count === 1 ? 'regalo elegido' : 'regalos elegidos'}` : `${count} ${count === 1 ? 'combo' : 'combos'} ${choice === 'two' ? '2x999' : '3x999'}`}</li>`).join('');
      const sizeChoices = new Map(Array.from(this.content.querySelectorAll('select'), select => [select.getAttribute('aria-label'), select.value]));
      this.content.innerHTML = `<div class="amaro-flow-head">${progressMarkup(this.choice, flow)}<h3 class="amaro-combos-heading" tabindex="-1">${flow.title}</h3><p class="amaro-combos-note">${flow.instruction}</p></div>
        ${completed ? `<ul class="amaro-flow-completed" aria-label="Completos en tu bolsa">${completed}</ul>` : ''}
        ${!flow.complete ? `<a class="amaro-flow-primary" href="${destination.href}">${this.choice === 'gift' && !state.collab ? 'Elegir mi par con regalo' : flow.filled ? 'Continuar mi combo' : 'Empezar este combo'} →</a>` : ''}
        ${cards ? `<details class="amaro-flow-suggestions"><summary>También puedes elegir aquí</summary><div class="amaro-combos-products">${cards}</div><a class="amaro-combos-more" href="${destination.href}">Ver toda la selección →</a></details>` : ''}
        <details class="amaro-flow-alternatives"><summary>${flow.complete ? 'Armar otro combo' : 'Ver otras promociones'}</summary><div class="amaro-combos-options">${choices.map(choice => `<button type="button" class="amaro-combos-option" data-combo-choice="${choice}" aria-pressed="${this.choice === choice}">${promoTitles[choice]}</button>`).join('')}</div>${flow.complete ? `<a class="amaro-combos-more" href="${destination.href}&nuevo=1">Empezar otro ${this.choice === 'gift' ? 'par con regalo' : this.choice === 'two' ? '2x999' : '3x999'} →</a>` : ''}</details>
        ${state.ignored ? '<p class="amaro-combos-note">Algunos productos de tu bolsa se compran por separado.</p>' : ''}
        ${state.lowerPriceGroups ? '<p class="amaro-combos-note">Si tus pares suman menos de $999, conservas el precio menor.</p>' : ''}
        ${rulesMarkup}`;
      for (const select of this.content.querySelectorAll('select')) {
        const previous = sizeChoices.get(select.getAttribute('aria-label'));
        if (previous && Array.from(select.options).some(option => option.value === previous)) select.value = previous;
      }
    }

    async addProduct(button) {
      if (AmaroCombos.adding) return;
      const select = button.parentElement.querySelector('select');
      const id = Number(button.dataset.comboVariant || select?.value);
      if (!id) {
        inlineSizeError(button, select);
        return;
      }
      const product = this.catalog.products.find((entry) => Number(entry.id) === Number(button.dataset.comboAdd));
      if (!product?.variants.some((variant) => Number(variant.id) === id && stock(variant) > 0)) return;
      const card = button.closest?.('[data-amaro-card]');
      AmaroCombos.adding = true;
      button.disabled = true;
      button.textContent = 'Agregando…';
      this.status.textContent = '';
      const main = document.getElementById('main-cart');
      const mainSection = main?.dataset.id;
      const sections = ['cart-drawer', ...(mainSection ? [mainSection] : [])];
      let added = false;
      try {
        this.catalog = await getCatalog(this.root, true).catch(() => {
          throw new Error('No pudimos confirmar las existencias. Comprueba tu conexión e intenta agregar de nuevo.');
        });
        const current = this.catalog.products.find((entry) => Number(entry.id) === Number(product.id));
        if (!availability(this.catalog.products)[this.choice] || !current?.variants.some((variant) => Number(variant.id) === id && stock(variant) > 0)) {
          this.render();
          throw new Error('Las existencias cambiaron. Revisa las combinaciones y tallas disponibles.');
        }
        const response = await fetch(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }], sections, sections_url: location.pathname }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'No se pudo agregar. Revisa la disponibilidad de tu talla.');
        added = true;
        window.dispatchEvent(new CustomEvent('amaro:cart-added'));
        // A null section is a rendering failure, not a failed add. Never submit it again.
        if (!result.sections?.['cart-drawer'] || (mainSection && !result.sections[mainSection])) { location.assign(`${this.root}cart`); return; }
        const drawer = document.querySelector('cart-drawer');
        drawer?.renderContents(result);
        if (this.matches?.('amaro-promo-carousel')) drawer?.showAddedItem(card?.querySelector('[data-combo-add]') || button, id);
        if (mainSection) {
          const html = new DOMParser().parseFromString(result.sections[mainSection], 'text/html');
          for (const selector of ['#main-cart', '#main-cart-summary']) {
            const current = document.querySelector(selector), fresh = html.querySelector(selector);
            if (current && fresh) current.innerHTML = fresh.innerHTML;
          }
        }
        const scope = mainSection ? document.querySelector('#main-cart amaro-combos') : drawer?.querySelector('amaro-combos');
        const status = scope?.querySelector('[data-combo-status]');
        if (status) status.textContent = '';
        if (mainSection || drawer?.classList.contains('open')) scope?.querySelector('.amaro-combos-heading')?.focus({ preventScroll: true });
        if (this.afterProductAdded) await this.afterProductAdded(product);
      } catch (error) {
        if (added) { location.assign(`${this.root}cart`); return; }
        if (this.isConnected) this.status.textContent = error.message || 'No se pudo agregar. Intenta de nuevo.';
      } finally {
        AmaroCombos.adding = false;
        if (button.isConnected) { button.disabled = false; button.textContent = 'Agregar'; }
      }
    }
  }
  class AmaroCollabGift extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.root = this.dataset.root || '/';
      this.abort = new AbortController();
      this.catalogListener = (catalog) => this.applyCatalog(catalog);
      catalogListeners.add(this.catalogListener);
      const refreshVisible = () => {
        if (document.visibilityState === 'visible' && Date.now() - catalogCheckedAt >= 30000) this.refresh();
      };
      document.addEventListener('visibilitychange', refreshVisible, { signal: this.abort.signal });
      window.addEventListener('focus', refreshVisible, { signal: this.abort.signal });
      window.addEventListener('online', refreshVisible, { signal: this.abort.signal });
      this.stockTimer = setInterval(refreshVisible, 300000);
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[href*="view=amaro-regalos"]');
        if (!link || !this.dataset.productId) return;
        const product = this.catalog?.products.find((entry) => Number(entry.id) === Number(this.dataset.productId));
        const variantId = this.closest('.product-media-info-inner')?.querySelector('product-form [name="id"]')?.value;
        const variant = product?.variants.find((entry) => Number(entry.id) === Number(variantId));
        const size = variant?.options.find((option) => /^\d{2}(?:\.\d)?$/.test(String(option)));
        if (size) { const url = new URL(link.href, location.origin); url.searchParams.set('talla', size); link.href = url.href; }
      }, { signal: this.abort.signal });
      this.refresh();
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearInterval(this.stockTimer);
      catalogListeners.delete(this.catalogListener);
    }
    applyCatalog(catalog) {
      if (!this.isConnected) return;
      this.catalog = catalog;
      const products = catalog?.products || [];
      const product = products.find((entry) => Number(entry.id) === Number(this.dataset.productId) && entry.group === 'collab');
      this.hidden = !product?.variants.some((variant) => stock(variant) > 0) || !products.some((entry) => entry.group === 'small' && entry.variants.some((variant) => stock(variant) > 0));
    }
    async refresh() {
      try { this.applyCatalog(await getCatalog(this.root)); }
      catch (_) { if (!this.catalog) this.applyCatalog(null); }
    }
  }
  // Maximum saving with companions that can actually be bought right now.
  const productOffers = (product, variant, products) => {
    const result = { gift: null, two: null, three: null };
    if (!product || !variant || !stock(variant)) return result;
    const companions = (group, reserveCurrent = false) => products
      .filter(entry => entry.group === group)
      .flatMap(entry => entry.variants.flatMap(option => {
        const reserved = reserveCurrent && Number(option.id) === Number(variant.id) ? 1 : 0;
        return Array(Math.min(2, Math.max(0, stock(option) - reserved))).fill(Number(option.price));
      })).sort((a, b) => b - a);
    const small = companions('small', product.group === 'small');
    const large = companions('large');
    const price = Number(variant.price);
    if (product.group === 'collab' && small.length) result.gift = small[0];
    if (product.group === 'large' && small.length) result.two = Math.max(0, price + small[0] - 99900);
    if (product.group === 'small') {
      if (large.length) result.two = Math.max(0, price + large[0] - 99900);
      if (small.length >= 2) result.three = Math.max(0, price + small[0] + small[1] - 99900);
    }
    return result;
  };
  class AmaroProductPromos extends AmaroCollabGift {
    connectedCallback() {
      this.explicitSizes = new Map();
      super.connectedCallback();
      this.closest('.section-main-product,#product-quick-view-drawer')?.addEventListener('change', event => {
        const field = event.target.closest('[data-amaro-pdp-options] fieldset:not(.color-swatches-box)');
        if (field) this.explicitSizes.set(field.querySelector('legend strong')?.textContent, field.querySelector('select')?.value || field.querySelector('input:checked')?.value);
        if (event.target.closest('[data-amaro-pdp-options]')) this.applyCatalog(this.catalog);
      }, { signal: this.abort.signal });
    }
    applyCatalog(catalog) {
      if (!this.isConnected) return;
      this.catalog = catalog;
      const products = catalog?.products || [];
      const product = products.find(entry => Number(entry.id) === Number(this.dataset.productId));
      const picker = this.closest('.product-media-info-inner')?.querySelector('[data-amaro-pdp-options]');
      const options = Array.from(picker?.querySelectorAll('fieldset') || [], fieldset => fieldset.querySelector('select')?.value || fieldset.querySelector('input:checked')?.value);
      const variant = product?.variants.find(entry => options.length && entry.options.every((option, index) => String(option) === options[index])) || (!options.length ? product?.variants.find(entry => stock(entry) > 0) : null);
      const offers = productOffers(product, variant, products);
      let visible = false;
      for (const link of this.querySelectorAll('[data-promo-option]')) {
        const choice = link.dataset.promoOption;
        const saving = offers[choice];
        link.hidden = saving === null;
        if (link.hidden) continue;
        visible = true;
        const destination = selectionDestination(choice, { large: product.group === 'large' }, this.root);
        const url = new URL(destination.href, location.origin);
        url.searchParams.set('origen', this.dataset.productId);
        const sizeFields = Array.from(picker?.querySelectorAll('fieldset:not(.color-swatches-box)') || []);
        if (sizeFields.length && sizeFields.every(field => this.explicitSizes.get(field.querySelector('legend strong')?.textContent) === (field.querySelector('select')?.value || field.querySelector('input:checked')?.value))) url.searchParams.set('variante', variant.id);
        const size = variant.options.find(option => /^\d{2}(?:\.\d)?$/.test(String(option)));
        if (size) url.searchParams.set('talla', size);
        link.href = url.href;
        const description = link.querySelector('[data-promo-description]');
        if (description) description.textContent = product.group === 'small' ? description.dataset.smallCopy : description.dataset.largeCopy;
        const label = link.querySelector('[data-promo-saving]');
        label.hidden = saving <= 0;
        label.textContent = saving > 0 ? this.dataset.savingTemplate.replace('__AMOUNT__', money(saving, catalog.currency)) : '';
      }
      this.hidden = !visible;
    }
  }
  class AmaroPromoCarousel extends AmaroCollabGift {
    connectedCallback() {
      this.choice = this.dataset.promo;
      this.status = this.querySelector?.('[data-combo-status]');
      super.connectedCallback();
      this.addEventListener('click', (event) => {
        const size = event.target.closest('[data-context-size]');
        if (size && !size.disabled) {
          size.closest('[data-amaro-card]').dataset.selectedSize = size.dataset.contextSize;
          this.renderCards();
        }
        const button = event.target.closest('[data-combo-add]');
        if (button) this.addProduct(button);
        if (event.target.closest('[data-amaro-color]')) requestAnimationFrame(() => { if (this.isConnected) this.renderCards(); });
      }, { signal: this.abort.signal });
    }
    applyCatalog(catalog) {
      if (!this.isConnected) return;
      this.catalog = catalog;
      this.hidden = !catalog || availability(catalog.products || [])[this.dataset.promo] !== true;
      if (!this.hidden) this.renderCards();
      this.removeAttribute('data-promo-loading');
      this.removeAttribute('aria-busy');
      this.removeAttribute('inert');
    }
    renderCards() {
      if (!this.catalog) return;
      for (const card of this.querySelectorAll?.('[data-amaro-card]') || []) {
        const id = Number(card.querySelector('[data-amaro-product-id]')?.dataset.amaroProductId);
        const product = this.catalog.products.find((entry) => Number(entry.id) === id && entry.variants.some((variant) => stock(variant) > 0));
        renderCardPurchase(card, product);
      }
    }
    render() { this.applyCatalog(this.catalog); }
    unavailable() { this.applyCatalog(null); }
    async addProduct(button) { return AmaroCombos.prototype.addProduct.call(this, button); }
    async afterProductAdded() { this.status.textContent = ''; }
  }
  class AmaroContextualSelection extends AmaroCollabGift {
    connectedCallback() {
      this.mode = this.dataset.context;
      this.sequence = 0;
      this.summary = this.querySelector('[data-context-summary]');
      this.grid = this.querySelector('[data-context-grid]');
      this.status = this.querySelector('[data-combo-status]');
      const params = new URLSearchParams(location.search);
      this.originId = params.get('origen');
      this.originVariantId = params.get('variante');
      this.startAnother = params.get('nuevo') === '1';
      this.requestedSize = /^\d{2}(?:\.\d)?$/.test(params.get('talla') || '') ? params.get('talla') : '';
      try { this.items = JSON.parse(this.querySelector('[data-context-cart]').textContent); } catch (_) { this.items = []; }
      super.connectedCallback();
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-flow-skip-origin]')) { this.originId = null; this.originVariantId = null; this.render(); return; }
        if (event.target.closest('[data-flow-retry]')) { this.refresh(); return; }
        const size = event.target.closest('[data-context-size]');
        if (size && !size.disabled) {
          size.closest('[data-amaro-card]').dataset.selectedSize = size.dataset.contextSize;
          this.renderCards();
        }
        const button = event.target.closest('[data-combo-add]');
        if (button) this.addProduct(button);
        if (event.target.closest('[data-amaro-color]')) requestAnimationFrame(() => { if (this.isConnected) this.renderCards(); });
      }, { signal: this.abort.signal });
      window.addEventListener('pageshow', (event) => { if (event.persisted) this.refresh(); }, { signal: this.abort.signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.refresh(event.cartState));
    }
    disconnectedCallback() { this.sequence++; this.unsubscribe?.(); super.disconnectedCallback(); }
    async refresh(cart) {
      const sequence = ++this.sequence;
      try {
        const cartRequest = Array.isArray(cart?.items) ? Promise.resolve(cart) : this.loaded ? fetch(`${this.root}cart.js`, { cache: 'no-store' }).then((response) => { if (!response.ok) throw new Error('cart'); return response.json(); }) : Promise.resolve({ items: this.items });
        const [current, catalog] = await Promise.all([cartRequest, getCatalog(this.root)]);
        if (!this.isConnected || sequence !== this.sequence) return;
        this.items = current.items;
        this.loaded = true;
        this.applyCatalog(catalog);
      } catch (_) { if (this.isConnected && sequence === this.sequence && !this.catalog) this.applyCatalog(null); }
    }
    applyCatalog(catalog) {
      if (!this.isConnected) return;
      this.hidden = false;
      this.catalog = catalog;
      if (!catalog) {
        this.grid.hidden = true;
        this.summary.innerHTML = `<p>No pudimos cargar la selección. Tus pares siguen en la bolsa.</p><div class="amaro-context-actions"><button class="amaro-flow-primary" type="button" data-flow-retry>Reintentar</button><a class="amaro-text-link" href="${this.root}cart">Ver mi bolsa →</a><a class="amaro-text-link" href="${this.root}collections/all">Ver productos →</a></div>`;
        return;
      }
      this.render();
    }
    unavailable() { this.applyCatalog(null); }
    render() {
      if (!this.catalog) return;
      const context = selectionContext(this.mode, this.items, this.catalog.products, this.originId);
      this.context = context; this.choice = context.choice; rememberChoice(this.choice);
      const { state, chosen, origin } = context;
      const flow = journey(this.choice, state);
      this.originPending = Boolean(origin && !this.items.some(item => Number(item.product_id) === Number(origin.id)));
      const complete = flow.complete && !this.startAnother && !this.originPending;
      if (this.originPending) { flow.filled = 0; flow.title = `Empieza con ${origin.title}`; flow.instruction = 'Elige tu talla y agrega este par. Después podrás completar tu promoción aquí.'; }
      else if (this.startAnother && flow.complete) { flow.filled = 0; flow.title = this.choice === 'gift' ? 'Elige otro par con regalo' : 'Elige tu primer par'; flow.instruction = 'Tu combo anterior sigue en la bolsa. Empieza otro con los pares de la selección.'; }
      this.context.complete = complete;
      this.toggleAttribute('data-origin-step', this.originPending);
      this.sizes = new Set(state.sizes); if (!this.sizes.size && this.requestedSize) this.sizes.add(this.requestedSize);
      const heading = this.querySelector('h1'); if (heading) heading.textContent = flow.title;
      const eyebrow = this.querySelector('.amaro-section-heading .amaro-eyebrow'); if (eyebrow) eyebrow.textContent = 'ARMA TU COMBO';
      const destination = selectionDestination(this.choice, { large: 0 }, this.root);
      const choices = Object.keys(promoTitles).filter(choice => availability(this.catalog.products)[choice]);
      const chosenText = chosen.map(item => `<li>${escape(item.product_title || item.title)}${item.quantity > 1 ? ` × ${item.quantity}` : ''}</li>`).join('');
      this.summary.innerHTML = `${progressMarkup(this.choice, flow)}<p class="amaro-context-instruction">${flow.instruction}</p>
        <div class="amaro-context-actions">${complete ? `<a class="amaro-flow-primary" href="${this.root}cart">Revisar mi bolsa →</a><a class="amaro-text-link" href="${destination.href}&nuevo=1">Armar otro ${this.choice === 'three' ? '3x999' : this.choice === 'two' ? '2x999' : 'par con regalo'} →</a>` : `<a class="amaro-text-link" href="${this.root}cart">Ver mi bolsa →</a>${this.originPending ? '<button class="amaro-flow-text-button" type="button" data-flow-skip-origin>Elegir otro modelo</button>' : ''}`}</div>
        <details class="amaro-flow-alternatives"><summary>Ver detalles y otras promociones</summary>${chosenText ? `<p>En tu bolsa</p><ul>${chosenText}</ul>` : ''}<div class="amaro-flow-links">${choices.filter(choice => choice !== this.choice).map(choice => `<a href="${selectionDestination(choice, state, this.root).href}">${promoTitles[choice]} →</a>`).join('')}</div><p>Los descuentos se calculan automáticamente. Cada par participa en una promoción y puedes elegir tallas diferentes.</p></details>`;
      const available = availability(this.catalog.products)[this.choice];
      this.grid.hidden = complete || !available;
      this.querySelector('.amaro-rail-footer')?.setAttribute('hidden', '');
      if (!available && !complete) {
        this.summary.insertAdjacentHTML('beforeend', `<p class="amaro-combos-status">Esta promoción no tiene pares disponibles ahora. Puedes elegir otra promoción o seguir con tu bolsa.</p><a class="amaro-text-link" href="${this.root}collections/all">Ver todos los productos →</a>`);
      }
      const empty = this.querySelector('[data-context-empty]'); if (empty) empty.hidden = true;
      if (!this.grid.hidden) this.renderCards();
    }

    renderCards() {
      if (!this.catalog || !this.context) return;
      let visible = 0;
      for (const card of this.grid.querySelectorAll('[data-amaro-card]')) {
        const info = card.querySelector('[data-amaro-product-id]');
        const ids = [Number(info?.dataset.amaroProductId), ...Array.from(card.querySelectorAll('[data-amaro-color]'), (button) => Number(button.dataset.amaroColor))];
        const products = this.catalog.products.filter(product => ids.includes(Number(product.id)) && (this.originPending ? Number(product.id) === Number(this.context.origin.id) : product.group === this.context.group) && product.variants.some(variant => stock(variant) > 0));
        card.hidden = !products.length;
        if (card.hidden) continue;
        visible++;
        card.style.order = products.some((product) => product.variants.some((variant) => stock(variant) > 0 && variant.options.some((option) => this.sizes.has(String(option))))) ? 0 : 1;
        const product = products.find((entry) => Number(entry.id) === Number(info?.dataset.amaroProductId));
        const carriedVariant = this.originPending && product?.variants.find(variant => Number(variant.id) === Number(this.originVariantId) && stock(variant) > 0);
        if (carriedVariant && Number(product.id) !== 15877783257374 && card.dataset.carriedVariant !== String(carriedVariant.id)) {
          card.dataset.selectedSize = carriedVariant.options.find(option => /^\d{2}(?:\.\d)?$/.test(String(option)));
          card.dataset.carriedVariant = String(carriedVariant.id);
        }
        renderCardPurchase(card, product);
        if (carriedVariant && Number(product.id) === 15877783257374) {
          card.querySelector('[data-context-buy]').innerHTML = `<div class="amaro-combos-buy"><span class="amaro-context-selection">${escape(carriedVariant.title)}</span><button type="button" data-combo-add="${product.id}" data-combo-variant="${carriedVariant.id}">Agregar</button></div><button type="button" class="amaro-flow-text-button" data-change-carried-sizes>Cambiar tallas</button>`;
          card.querySelector('[data-change-carried-sizes]').addEventListener('click', () => card.querySelector('[data-quickview]')?.click());
        }
        const price = card.querySelector('.amaro-card-price');
        const isGift = this.choice === 'gift' && !this.originPending && this.context.state.collab > 0 && product?.group === 'small';
        if (price) { if (!price.dataset.currentPrice) price.dataset.currentPrice = price.textContent; price.textContent = isGift ? 'Tu regalo · $0' : price.dataset.currentPrice; }
        const add = card.querySelector('[data-combo-add]'); if (add && isGift) add.textContent = 'Elegir regalo';
      }
      let empty = this.querySelector('[data-context-empty]');
      if (!empty) { empty = document.createElement('p'); empty.setAttribute('data-context-empty', ''); this.grid.after(empty); }
      empty.textContent = 'Por ahora no hay pares disponibles para esta selección.';
      empty.hidden = visible > 0;
    }
    async addProduct(button) { return AmaroCombos.prototype.addProduct.call(this, button); }
    async afterProductAdded(product) {
      this.startAnother = false;
      await this.refresh();
      this.status.textContent = '';
      this.summary.focus({ preventScroll: true }); this.querySelector('.amaro-section-heading').scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }
  const cartSignature = (items) => {
    // Shopify changes line keys when discounts apply. Match the purchased selection instead.
    const lines = new Map();
    for (const item of items || []) {
      const properties = Object.entries(item.properties || {}).filter(([name]) => !['_bndlr', '__'].includes(name)).sort(([a], [b]) => a.localeCompare(b));
      const identity = JSON.stringify([String(item.variant_id || item.id), item.selling_plan_allocation?.selling_plan?.id || null, properties, Number(item.original_price ?? item.price)]);
      lines.set(identity, (lines.get(identity) || 0) + Number(item.quantity));
    }
    return JSON.stringify(Array.from(lines).sort(([a], [b]) => a.localeCompare(b)));
  };
  let bundleTotals, totalsTimer;
  const recalculateTotals = () => {
    clearTimeout(totalsTimer);
    totalsTimer = setTimeout(() => {
      if (document.visibilityState !== 'hidden' && typeof window.bndlr?.updateCartDiscounts === 'function') window.bndlr.updateCartDiscounts();
    }, 120);
  };
  document.addEventListener('bundler:total_cart_values', (event) => {
    const data = event.detail;
    if (!Array.isArray(data?.original_cart_object?.items)) return;
    const original = Number(data.total_amount_original), total = Number(data.total_amount_discounted);
    if (!Number.isFinite(total) || !Number.isFinite(original) || total < 0 || total > original) return;
    bundleTotals = { signature: cartSignature(data.original_cart_object.items), original, total };
    document.querySelectorAll('amaro-cart-total').forEach((element) => element.render());
    document.querySelectorAll('amaro-shipping-announcement').forEach((element) => element.render());
  });
  document.addEventListener('bundler:loaded', () => {
    // Native Bundler still initializes normally; an empty cart needs no extra recalculation.
    if (Array.from(document.querySelectorAll('amaro-cart-total,amaro-shipping-announcement')).some(element => element.cart?.items?.length)) recalculateTotals();
  });
  class AmaroCollectionPurchase extends HTMLElement {
    connectedCallback() {
      this.root = this.dataset.root || '/';
      this.status = this.querySelector('[data-combo-status]');
      this.abort = new AbortController();
      this.initializeCards();
      this.addEventListener('click', event => {
        const size = event.target.closest('[data-context-size]');
        if (size && !size.disabled) {
          const card = size.closest('[data-amaro-card]');
          card.dataset.selectedSize = size.dataset.contextSize;
          renderCardPurchase(card, this.cardProduct(card));
        }
        const button = event.target.closest('[data-combo-add]');
        if (button) this.addProduct(button);
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
    cardProduct(card) {
      return { id: Number(card.dataset.amaroProduct), title: card.querySelector('.amaro-card-title')?.textContent.trim() || '', variants: Array.from(card.querySelectorAll('[data-context-size]'), size => ({ id: Number(size.dataset.variantId), available: !size.disabled })) };
    }
    initializeCards() {
      for (const card of this.querySelectorAll('[data-amaro-card]')) {
        if (card.querySelector('[data-context-buy]')) continue;
        const product = this.cardProduct(card);
        renderCardPurchase(card, product);
        if (!product.variants.length) {
          const button = card.querySelector('[data-combo-add]');
          if (button) { button.disabled = true; button.textContent = 'Agotado'; }
        }
      }
    }
    async addProduct(button) {
      if (AmaroCombos.adding) return;
      const card = button.closest('[data-amaro-card]');
      const id = Number(button.dataset.comboVariant);
      if (!id) {
        inlineSizeError(button);
        return;
      }
      AmaroCombos.adding = true; button.disabled = true; button.textContent = 'Agregando…';
      this.status.textContent = '';
      let added = false;
      try {
        const fresh = await fetch(`${this.root}products/${encodeURIComponent(card.dataset.amaroHandle)}.js`, { cache: 'no-store' });
        if (!fresh.ok) throw new Error('No pudimos confirmar las existencias. Intenta de nuevo.');
        const product = await fresh.json();
        if (!product.variants?.some(v => Number(v.id) === id && v.available)) throw new Error('Esta talla ya no está disponible. Elige otra.');
        const response = await fetch(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: ['cart-drawer'], sections_url: location.pathname }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'No se pudo agregar. Revisa la talla e intenta de nuevo.');
        added = true;
        window.dispatchEvent(new CustomEvent('amaro:cart-added'));
        if (!result.sections?.['cart-drawer']) { location.assign(`${this.root}cart`); return; }
        const drawer = document.querySelector('cart-drawer');
        drawer?.renderContents(result);
        drawer?.showAddedItem(button, id);
        this.status.textContent = '';
      } catch (error) {
        if (added) { location.assign(`${this.root}cart`); return; }
        if (this.isConnected) this.status.textContent = error.message || 'No se pudo agregar. Intenta de nuevo.';
      } finally {
        AmaroCombos.adding = false;
        if (button.isConnected) { button.disabled = false; button.textContent = 'Agregar'; }
      }
    }
  }

  class AmaroShippingAnnouncement extends HTMLElement {
    connectedCallback() {
      try { this.cart = JSON.parse(this.querySelector('script').textContent); } catch (_) { return; }
      this.root = this.dataset.root || '/';
      this.output = this.querySelector('[data-shipping-announcement-message]');
      this.abort = new AbortController();
      this.sequence = 0;
      this.catalogListener = catalog => { this.catalog = catalog; if (this.isConnected) this.render(); };
      catalogListeners.add(this.catalogListener);
      if (this.cart.items.length) getCatalog(this.root).then(this.catalogListener).catch(() => {});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => this.refresh(event.cartState));
      window.addEventListener('amaro:cart-added', () => this.refresh(), { signal: this.abort.signal });
      window.addEventListener('pageshow', event => { if (event.persisted) this.refresh(); }, { signal: this.abort.signal });
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.refresh(); }, { signal: this.abort.signal });
      this.render();
      if (this.cart.items.length) recalculateTotals();
    }
    disconnectedCallback() {
      this.sequence++; this.abort?.abort(); this.unsubscribe?.(); catalogListeners.delete(this.catalogListener);
    }
    async refresh(cart) {
      const sequence = ++this.sequence;
      try {
        if (typeof cart?.total_price !== 'number' || !Array.isArray(cart.items)) {
          const response = await fetch(`${this.root}cart.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error('cart');
          cart = await response.json();
        }
        if (!this.isConnected || sequence !== this.sequence) return;
        this.cart = cart;
        if (this.cart.items.length && !this.catalog) getCatalog(this.root).then(this.catalogListener).catch(() => {});
        this.render();
        recalculateTotals();
      } catch (_) { /* Keep the last confirmed cart during a transient failure. */ }
    }
    render() {
      if (!this.cart || !this.output) return;
      if (!this.cart.items.length) {
        this.output.textContent = this.dataset.default;
        this.dataset.freeShipping = 'false';
        return;
      }
      const matched = bundleTotals?.signature === cartSignature(this.cart.items) && bundleTotals.original === Number(this.cart.original_total_price);
      const state = this.catalog ? plan(this.cart.items, this.catalog.products) : null;
      const pending = !matched && this.cart.items.length > 0 && (!state || state.gifts > 0 || state.pairs > 0 || state.triples > 0);
      const total = matched ? Math.min(bundleTotals.total, Number(this.cart.total_price)) : Number(this.cart.total_price);
      const qualifies = !pending && (this.cart.currency || 'MXN') === 'MXN' && total >= Number(this.dataset.threshold || 199800);
      this.output.textContent = qualifies ? '¡Tienes envío gratis!' : this.dataset.default;
      this.dataset.freeShipping = String(qualifies);
    }
  }

  class AmaroCartTotal extends HTMLElement {
    connectedCallback() {
      try { this.cart = JSON.parse(this.querySelector('script').textContent); } catch (_) { return; }
      this.signature = cartSignature(this.cart.items);
      this.output = this.querySelector('[data-cart-total-value]');
      this.note = this.querySelector('[data-cart-total-note]');
      this.abort = new AbortController();
      const refresh = () => { if (document.visibilityState === 'visible') recalculateTotals(); };
      document.addEventListener('visibilitychange', refresh, { signal: this.abort.signal });
      window.addEventListener('online', refresh, { signal: this.abort.signal });
      this.render();
      if (this.cart.items.length) recalculateTotals();
    }
    disconnectedCallback() { this.abort?.abort(); }
    render() {
      const matched = bundleTotals?.signature === this.signature && bundleTotals.original === Number(this.cart.original_total_price);
      const discounted = matched && bundleTotals.total < Number(this.cart.total_price);
      const total = discounted ? bundleTotals.total : Number(this.cart.total_price);
      this.output.textContent = new Intl.NumberFormat('es-MX', { style: 'currency', currency: this.cart.currency || 'MXN' }).format(total / 100) + ' ' + (this.cart.currency || 'MXN');
      const state = cachedCatalog ? plan(this.cart.items, cachedCatalog.products) : null;
      const pending = !matched && state && (state.gifts || state.pairs || state.triples) && Number(this.cart.total_price) === Number(this.cart.original_total_price);
      this.note.textContent = pending ? 'Calculando promociones…' : 'Promociones incluidas';
      this.note.hidden = !pending && total >= Number(this.cart.original_total_price);
      this.dataset.discounted = String(total < Number(this.cart.original_total_price));
    }
  }
  customElements.define('amaro-cart-total', AmaroCartTotal);
  customElements.define('amaro-shipping-announcement', AmaroShippingAnnouncement);
  customElements.define('amaro-collection-purchase', AmaroCollectionPurchase);
  customElements.define('amaro-combos', AmaroCombos);
  customElements.define('amaro-collab-gift', AmaroCollabGift);
  customElements.define('amaro-product-promos', AmaroProductPromos);
  customElements.define('amaro-promo-carousel', AmaroPromoCarousel);
  customElements.define('amaro-contextual-selection', AmaroContextualSelection);

  // Announce only a newly completed allocation, never an existing cart on page load.
  const combinationOptions = (state, products, root) => {
    const available = availability(products);
    const options = [];
    if (available.gift && state.collab) options.push({ ...selectionDestination('gift', state, root), choice: 'gift', label: 'Elegir mi regalo', description: state.collab === 1 ? 'Te falta elegir tu par de regalo.' : `Tienes ${state.collab} regalos por elegir.` });
    if (available.two && !state.collab) options.push({ ...selectionDestination('two', state, root), choice: 'two', label: 'Continuar 2x999', description: state.large || state.small ? 'Te falta un par para completar 2x999.' : 'Puedes empezar otro combo 2x999.' });
    if (available.three && !state.collab && !state.large) options.push({ ...selectionDestination('three', state, root), choice: 'three', label: 'Continuar 3x999', description: state.small ? `Te ${state.small === 2 ? 'falta 1 par' : 'faltan 2 pares'} para completar 3x999.` : 'Puedes empezar un combo 3x999.' });
    const preferred = preferredChoice(state, available); options.sort((a, b) => Number(b.choice === preferred) - Number(a.choice === preferred));
    return options;
  };
  let previousCombos, previousItemCount, pendingAddition = false, noticeSequence = 0;
  const checkCompletedCombos = async (cart, announce = true) => {
    const sequence = ++noticeSequence;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const current = typeof cart?.item_count === 'number' && Array.isArray(cart.items)
        ? cart : await fetch(`${root}cart.js`, { cache: 'no-store' }).then(r => { if (!r.ok) throw new Error('cart'); return r.json(); });
      const catalog = current.items.length ? await getCatalog(root) : { products: [] };
      if (sequence !== noticeSequence) return;
      const state = plan(current.items, catalog.products);
      const counts = { gifts: state.gifts, pairs: state.pairs, triples: state.triples };
      const options = combinationOptions(state, catalog.products, root);
      const added = pendingAddition;
      pendingAddition = false;
      const removed = previousItemCount !== undefined && current.item_count < previousItemCount;
      if (removed) window.dispatchEvent(new CustomEvent('amaro:cart-removed'));
      previousItemCount = current.item_count;
      let completed = false;
      if (announce && previousCombos && !removed) {
        const names = [counts.gifts > previousCombos.gifts && 'Tu par + regalo', counts.pairs > previousCombos.pairs && 'Tu combo 2x999', counts.triples > previousCombos.triples && 'Tu combo 3x999'].filter(Boolean);
        if (names.length) {
          completed = true;
          window.dispatchEvent(new CustomEvent('amaro:combo-complete', { detail: { message: `${names.join(' y ')} ${names.length > 1 ? 'están completos' : 'está completo'}. Revisa el total en tu bolsa.`, options } }));
        }
      }
      if (added && !completed && !removed && current.item_count) window.dispatchEvent(new CustomEvent('amaro:cart-options', { detail: { options } }));
      previousCombos = counts;
    } catch (_) {
      if (sequence !== noticeSequence) return;
      if (pendingAddition) window.dispatchEvent(new CustomEvent('amaro:cart-options', { detail: { options: [] } }));
      pendingAddition = false;
      // An uncertain cart must never trigger a completion notice or advertise stock.
    }
  };
  const startCompletionNotices = () => {
    let initialCart;
    try { initialCart = JSON.parse(document.querySelector('amaro-shipping-announcement script')?.textContent || 'null'); } catch (_) {}
    if (Array.isArray(initialCart?.items)) initialCart.item_count = initialCart.items.reduce((count, item) => count + Number(item.quantity || 0), 0);
    checkCompletedCombos(initialCart, false);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, event => checkCompletedCombos(event.cartState));
    const added = () => { pendingAddition = true; checkCompletedCombos(); };
    window.addEventListener('amaro:cart-added', added);
    window.addEventListener('amaro:product-added', added);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startCompletionNotices, { once: true });
  else startCompletionNotices();
})();
